import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, USERSROLE } from 'types';
import { Button, InlineLoading } from 'carbon-components-react';
import { Add16 } from '@carbon/icons-react';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { getFeatureServerUrl } from 'services/map.service';
import FeatureLayerURL from './components/featureLayerUrl';
import CredentialForm from './components/credentialForm';
import {
  getArcgisPasswordStart,
  resetArcgisPassword,
  addArcgisPasswordStart
} from 'redux/arcgis';
import { useComponentMount } from 'utilities/hooks.util';

const AOIShapeGIS = 'AreasOfInterest';
const EnquiryShapeGIS = 'Enquiries';
const EnquiryPointGIS = 'Enquiries_pts';
const EnquiryHexbinGIS = 'Enquiries_hexbins';
const ReferralShapeGIS = 'Referrals';
const ReferralHexbinGIS = 'Referrals_hexbins';

const getUserGISList = (role: string) => {
  switch (role) {
    case USERSROLE.AOVIEWER:
    case USERSROLE.AOADMIN: {
      return [AOIShapeGIS, ReferralShapeGIS, ReferralHexbinGIS];
    }
    case USERSROLE.DBYDADMIN: {
      return [EnquiryHexbinGIS, AOIShapeGIS];
    }
    default: {
      return [EnquiryShapeGIS, EnquiryPointGIS];
    }
  }
};

const getGISLabel = (
  service: string,
  role: string,
  organisationName: string
) => {
  switch (service) {
    case EnquiryShapeGIS: {
      return `${
        role === USERSROLE.DBYDADMIN ? organisationName : 'Your'
      } enquiries (polygons)`;
    }
    case EnquiryPointGIS: {
      return `${
        role === USERSROLE.DBYDADMIN ? organisationName : 'Your'
      } enquiries (points)`;
    }
    case EnquiryHexbinGIS: {
      return `${organisationName} enquiries (hex bins)`;
    }
    case AOIShapeGIS: {
      return `${organisationName} Area of Interest`;
    }
    case ReferralShapeGIS: {
      return `${organisationName} Referrals (polygons)`;
    }
    case ReferralHexbinGIS: {
      return `${organisationName} Referrals (hex bins)`;
    }
    default: {
      return '';
    }
  }
};

const GISIntegration: React.FC<Props> = ({
  user,
  arcgisUsername,
  arcgisPassword,
  arcgisProcessing
}) => {
  const dispatch = useDispatch();
  const [userRole, setUserRole] = useState<string>('');
  const [isFocus, setIsFocus] = useState<boolean>(true);
  const [started, setStarted] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [organisationName, setOrganisationName] = useState<string>('');

  useEffect(() => {
    user?.role && setUserRole(user.role);
    user?.Organisation?.name && setOrganisationName(user.Organisation.name);
  }, [user]);

  useEffect(() => {
    arcgisUsername && setIsFocus(false);
  }, [arcgisUsername]);

  useEffect(() => {
    arcgisProcessing && setStarted(true);
  }, [arcgisProcessing]);

  useEffect(() => {
    ready && dispatch(getArcgisPasswordStart());
  }, [organisationName, ready, dispatch]);

  useComponentMount(() => {
    setReady(true);
    return () => {
      dispatch(resetArcgisPassword());
    };
  });

  return (
    <div
      id="GISIntegration"
      data-testid="GISIntegration"
      className="gis-integration"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="GIS integration"
        pageDescription="Include feature layers from SmarterWX Sentinel in your own mapping applications"
        enableHeaderGlobalBar={true}
      >
        <div className="gis-integration-container">
          <div
            className={`gis-integration-section credentials-section${
              started && !arcgisProcessing && isFocus ? ' is-focus' : ''
            }`}
          >
            <div className="gis-integration-section-item">
              <div className="title">Access credentials</div>
              <div className="desc">
                To include these layers in your mapping application, you will
                need to provide user credentials. These credentials have the
                same permissions as your user account.
              </div>
            </div>
            <div className="gis-integration-section-item">
              {arcgisUsername ? (
                <CredentialForm
                  username={arcgisUsername}
                  password={arcgisPassword}
                  processing={arcgisProcessing}
                />
              ) : (
                <>
                  {arcgisProcessing ? (
                    <InlineLoading
                      description={
                        arcgisProcessing === 'adding'
                          ? 'Generating...'
                          : 'Loading...'
                      }
                    />
                  ) : (
                    <>
                      {started && (
                        <Button
                          renderIcon={Add16}
                          onClick={() =>
                            dispatch(addArcgisPasswordStart(false))
                          }
                        >
                          Generate GIS credentials
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="gis-integration-section">
            <div className="gis-integration-section-item">
              <div className="title">Available GIS feature layers</div>
              <div className="desc">
                If you have generated user credentials to sign in, you can
                integrate these layers
              </div>
            </div>
            {getUserGISList('all').map((gis: string, inx: number) => {
              return (
                <div
                  key={`gis-all-${inx}`}
                  className="gis-integration-section-item"
                >
                  <FeatureLayerURL
                    url={getFeatureServerUrl(gis, true)}
                    label={getGISLabel(gis, userRole, organisationName)}
                  />
                </div>
              );
            })}
          </div>
          {(userRole === USERSROLE.AOADMIN ||
            userRole === USERSROLE.AOVIEWER ||
            userRole === USERSROLE.DBYDADMIN) && (
            <div className="gis-integration-section">
              {getUserGISList(userRole).map((gis: string, inx: number) => {
                return (
                  <div
                    key={`gis-admin-${inx}`}
                    className="gis-integration-section-item"
                  >
                    <FeatureLayerURL
                      url={getFeatureServerUrl(gis, true)}
                      label={getGISLabel(gis, userRole, organisationName)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

const mapStateToProps = ({ session, arcgisCache }: RootState) => ({
  user: session.currentUser,
  arcgisUsername: arcgisCache.accessCredentials?.username,
  arcgisPassword: arcgisCache.accessCredentials?.password,
  arcgisProcessing: arcgisCache.accessCredentials?.processing
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(GISIntegration));
